// src/features/executive/insight/widgets/SourceChannelDonut.tsx
import { Chip, Stack, Typography } from '@mui/material'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import { useTranslation } from 'react-i18next'
import CategoricalDistributionCard from './CategoricalDistributionCard'
import { formatVariance } from 'utils/executive.utils'
import { useSourceChannel } from '../hooks/use-source-channel'
import type { SourceChannelKey } from 'api/executive/executive.types'

const COLOR: Record<SourceChannelKey, string> = {
  facebook: '#1877f2',
  referral: '#4caf50',
  google: '#ea4335',
  line: '#06c755',
  walkin: '#9b59b6',
  other: '#95a5a6',
}

const I18N: Record<SourceChannelKey, string> = {
  facebook: 'EXECUTIVE.INSIGHT.SOURCE_CHANNEL.FACEBOOK',
  referral: 'EXECUTIVE.INSIGHT.SOURCE_CHANNEL.REFERRAL',
  google: 'EXECUTIVE.INSIGHT.SOURCE_CHANNEL.GOOGLE',
  line: 'EXECUTIVE.INSIGHT.SOURCE_CHANNEL.LINE',
  walkin: 'EXECUTIVE.INSIGHT.SOURCE_CHANNEL.WALKIN',
  other: 'EXECUTIVE.INSIGHT.SOURCE_CHANNEL.OTHER',
}

// Threshold between Country (10%) and DecisionFactors (30%). Front desk
// usually asks "how did you hear about us?" but it's still skippable.
const UNKNOWN_WARNING_PCT = 25

// Surface a non-'other' segment with the largest absolute variance. Source
// channels are volatile both ways — ad-spend changes, organic growth, etc.
const MOVER_MIN_VARIANCE = 0.2

function varianceOf(current: number, previous: number | null | undefined): number | null {
  if (previous == null || previous === 0) return null
  return (current - previous) / Math.abs(previous)
}

export default function SourceChannelDonut() {
  const { t } = useTranslation()
  const { data, loading, error, refetch } = useSourceChannel()

  const segments = data?.segments ?? []
  const total = data?.total ?? 0
  const previousTotal = data?.previousTotal ?? null
  const unknownCount = data?.unknownCount ?? 0

  // Data-quality nudge — NULL accessChannelId folded into 'other'.
  const unknownPct = total > 0 ? (unknownCount / total) * 100 : 0
  const showUnknownWarning = unknownPct > UNKNOWN_WARNING_PCT

  // Largest mover among non-'other' segments with previousCount > 0. Both
  // directions matter for marketing decisions.
  const mover = segments
    .filter((s) => s.key !== 'other' && s.previousCount != null && s.previousCount > 0)
    .map((s) => ({ s, v: varianceOf(s.count, s.previousCount) }))
    .filter((x) => x.v != null && Math.abs(x.v as number) >= MOVER_MIN_VARIANCE)
    .sort((a, b) => Math.abs(b.v as number) - Math.abs(a.v as number))[0]

  const notice = (showUnknownWarning || mover) ? (
    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
      {showUnknownWarning && (
        <Chip
          icon={<WarningAmberOutlinedIcon />}
          label={
            <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
              {t(
                'EXECUTIVE.INSIGHT.SOURCE_CHANNEL.UNSPECIFIED_WARNING',
                'ไม่ระบุช่องทางที่รู้จักคลินิก {{pct}}% — โปรดตรวจสอบการบันทึกข้อมูลในแบบฟอร์มลงทะเบียน',
                { pct: unknownPct.toFixed(0) }
              )}
            </Typography>
          }
          color="warning"
          variant="outlined"
          size="small"
          sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
        />
      )}
      {mover && (mover.v as number) >= 0 && (
        <Chip
          icon={<TrendingUpIcon />}
          label={
            <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
              {t(
                'EXECUTIVE.INSIGHT.SOURCE_CHANNEL.MOVER_UP',
                'ช่องทางมาแรง: {{label}} ▲ {{pct}}',
                {
                  label: t(I18N[mover.s.key]),
                  pct: formatVariance(mover.v as number),
                }
              )}
            </Typography>
          }
          color="info"
          variant="outlined"
          size="small"
          sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
        />
      )}
      {mover && (mover.v as number) < 0 && (
        <Chip
          icon={<TrendingDownIcon />}
          label={
            <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
              {t(
                'EXECUTIVE.INSIGHT.SOURCE_CHANNEL.MOVER_DOWN',
                'ช่องทางลดลง: {{label}} ▼ {{pct}}',
                {
                  label: t(I18N[mover.s.key]),
                  pct: formatVariance(mover.v as number),
                }
              )}
            </Typography>
          }
          color="warning"
          variant="outlined"
          size="small"
          sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
        />
      )}
    </Stack>
  ) : null

  return (
    <CategoricalDistributionCard
      title={t('EXECUTIVE.INSIGHT.SOURCE_CHANNEL.TITLE')}
      titleTooltip={t(
        'EXECUTIVE.INSIGHT.SOURCE_CHANNEL.SOURCE_TOOLTIP',
        'อ้างอิงจาก patients.accessChannelId ↔ access_channels.channelName — คำตอบในใบลงทะเบียนถูก map เป็น 6 กลุ่มผ่าน keyword:\n• Facebook ← facebook/fb/เฟส/เฟซ\n• LINE ← line/ไลน์\n• Google ← google/กูเกิ/website/seo/ค้นหา\n• แนะนำ ← แนะนำ/บอกต่อ/รีวิว/เพื่อน/ญาติ\n• Walk-in ← walk/เดินผ่าน/หน้าร้าน/ป้าย\n• อื่น ๆ ← ที่ไม่ตรง keyword + NULL'
      )}
      emptyMessage={t(
        'EXECUTIVE.INSIGHT.SOURCE_CHANNEL.EMPTY',
        'ไม่พบข้อมูลช่องทางในช่วงเวลาและเงื่อนไขที่เลือก'
      )}
      notice={notice}
      segments={segments.map((s) => ({
        ...s,
        label: t(I18N[s.key]),
      }))}
      total={total}
      previousTotal={previousTotal}
      colorMap={COLOR}
      centerLabel={t('EXECUTIVE.INSIGHT.TOTAL_PATIENTS')}
      unit={t('EXECUTIVE.INSIGHT.UNIT.PATIENT')}
      loading={loading}
      error={error}
      onRetry={refetch}
    />
  )
}
